import { createSlice, isAnyOf } from '@reduxjs/toolkit';

import { logDevMessage, getLocalStorageExpire, setLocalStorageExpire } from 'utils/utils';
import * as chatActions from 'store/slices/chat/chatSlice';
import {
  catchAppError,
  startOver,
  handleNetworkChange,
  resultsPageUnmounted,
} from '../../appActions';
import { UI_SLICE_NAME } from '../slicesNames';
import { executeSearch } from '../results/resultsThunks';
import * as uiConstants from './uiConstants';

const initialState = {
  profileModal: {
    // type can be any value from VALID_PROFILE_MODALS
    type: null,
    // data can be any
    data: null,
  },

  modals: {
    // error modals
    appError: false,

    // mobile search modals
    updateRegion: false,

    // misc modals
    highQuality: false,
    providersOfDistinction: false,
    mercyHealthRedirect: false,
    rewards: false,
    chat: false,
    filter: false,
    ClientConfigDevTools: false,
  },

  // other ui state
  isCssEnabled: true,
  returnFocusToKey: null,

  breakpoint: 'xs',
  showResultsMap: false,
  userHasMetOutOfPocketMaximum: false,
};

const uiSlice = createSlice({
  name: UI_SLICE_NAME,
  initialState,
  reducers: {
    openModal(state, action) {
      const modalName = action.payload;

      const currentOpenModal = Object.keys(state.modals).find((key) => state.modals[key] === true);

      const currentOpenModalPriority = uiConstants.modalPriorityMap[currentOpenModal] || 0;
      const modalToOpenPriority = uiConstants.modalPriorityMap[modalName] || 0;

      if (currentOpenModalPriority > modalToOpenPriority) return;

      // loop through all modals and open the matching one. Set all others to false, ensuring that multiple modals are not opened at once
      for (const modalKey in state.modals) {
        if (modalKey === modalName) {
          state.modals[modalKey] = true;
        } else {
          state.modals[modalKey] = false;
        }
      }
    },
    closeModal(state, action) {
      const modalToClose = action.payload;

      if (!modalToClose) {
        // if no modal key is passed, close all modals
        for (const modalKey in state.modals) {
          if (state.modals[modalKey] !== undefined) state.modals[modalKey] = false;
        }
        // also reset the profile modal
        state.profileModal = initialState.profileModal;
      } else if (state.modals[modalToClose] !== undefined) {
        state.modals[modalToClose] = false;
      } else {
        logDevMessage(`No modal with key ${modalToClose}`);
      }
    },
    toggleUserHasMetOutOfPocketMaximum(state) {
      state.userHasMetOutOfPocketMaximum = !state.userHasMetOutOfPocketMaximum;
    },
    setIsCssEnabled(state, action) {
      state.isCssEnabled = action.payload;
    },
    setReturnFocusToKey(state, action) {
      state.returnFocusToKey = action.payload;
    },
    closeFeedbackModal(state) {
      state.profileModal = initialState.profileModal;
    },
    promptFeedbackModal(state) {
      const feedbackModalAlreadyShown = Boolean(getLocalStorageExpire('feedbackPrompted'));
      if (!feedbackModalAlreadyShown) {
        state.profileModal = { type: 'feedback' };
        setLocalStorageExpire('feedbackPrompted', 'true', 3);
      }
    },
    closeProfileModal(state) {
      state.profileModal = initialState.profileModal;
    },
    openProfileModal(state, action) {
      let type;
      let data = null;

      if (typeof action.payload === 'string') {
        // in this case someone has passed dispatch(actions.ui.openProfileModal('feedback'))
        // meaning that no data was required
        type = action.payload;
      } else {
        // in this case someone has passed
        // dispatch(actions.ui.openProfileModal({ type: 'call', data: [] }))
        type = action.payload?.type;
        data = action.payload?.data;
      }

      if (uiConstants.VALID_PROFILE_MODALS.includes(type)) {
        state.profileModal.type = type;
        state.profileModal.data = data;

        if (type === uiConstants.PROFILE_MODAL_TYPES.FEEDBACK) {
          setLocalStorageExpire('feedbackPrompted', 'true', 3);
        }
      } else {
        logDevMessage(`Invalid profile modal type: ${type}`);
      }
    },
    openShareModal(state, action) {
      const { results, location } = action.payload;

      // validate payload
      if (!Array.isArray(results) || !results.length) throw new Error('Invalid results in payload');
      if (!location?.coords) throw new Error('Invalid location in payload');

      state.profileModal.type = uiConstants.PROFILE_MODAL_TYPES.SHARE;
      state.profileModal.data = { results, location };
    },
    breakpointChanged(state, action) {
      state.breakpoint = action.payload;
    },
    toggleShowResultsMap(state) {
      state.showResultsMap = !state.showResultsMap;
    },
    setShowResultsMap(state, action) {
      const showMap = action.payload;
      state.showResultsMap = showMap;
    },
  },
  extraReducers(builder) {
    builder.addCase(catchAppError, (state) => {
      state.modals.appError = true;
    });

    builder.addCase(startOver, (state) => {
      // this should handle all state resets that need to happen on a caught error, or when the logo is clicked
      state.modals.appError = false;
    });

    builder.addCase(handleNetworkChange, (state) => {
      state.modals.updateRegion = false;
    });

    builder.addCase(executeSearch.fulfilled, (state, action) => {
      if (uiConstants.lgUpBreakpoints.includes(state.breakpoint)) {
        // on desktop sized screens, automatically open the map when results come in
        const { response = {} } = action.payload;

        // only open the map if there is at least 1 returned result
        if (response.count) state.showResultsMap = true;
      }
    });

    builder.addCase(resultsPageUnmounted, (state) => {
      state.modals.filter = false;
    });

    builder.addMatcher(
      isAnyOf(chatActions.specialtySearchInPg, chatActions.goToHomepage),
      (state) => {
        state.modals.chat = false;
      }
    );
  },
});

export default uiSlice;
export const {
  openModal,
  closeModal,
  closeFeedbackModal,
  closeProfileModal,
  openProfileModal,
  openShareModal,
  setIsCssEnabled,
  setReturnFocusToKey,
  breakpointChanged,
  toggleShowResultsMap,
  setShowResultsMap,
  toggleUserHasMetOutOfPocketMaximum,
  promptFeedbackModal,
} = uiSlice.actions;
